import { PLAN } from '@/content/pricing';
import { DEMO_URL, SIGNUP_URL } from '@/lib/urls';

/**
 * Modalità di registrazione, decisa dal backend (Admin → Preferences →
 * `landing.signup_mode`) e letta da `GET /v1/landing/configuration`.
 * Cambia le CTA di tutto il sito senza deploy della landing.
 *
 * `demo_masked_trial_only` è la demo presentata come prova gratuita: il
 * bottone dice «Prova gratis» e apre la stessa prenotazione, con le parole
 * della prova — un'introduzione in videochiamata, alla fine della quale
 * l'account di prova lo attiviamo noi. La prova in autonomia non c'è.
 */
export type SignupMode = 'demo_only' | 'demo_and_trial' | 'trial_only' | 'demo_masked_trial_only';

/**
 * La modalità di quando non si sa: nell'HTML statico, mentre la
 * configurazione non è ancora arrivata, e soprattutto se il backend non
 * risponde affatto.
 *
 * È `trial_only` per una ragione sola: **la demo non si offre mai al buio**.
 * Prenotarla vuol dire creare un lead e leggere gli slot, cioè altre due
 * chiamate allo stesso backend che qui sta tacendo: una demo proposta e poi
 * non prenotabile è peggio di una demo non proposta. La prova invece porta
 * fuori di qui, all'app, e regge anche se questo backend è giù.
 *
 * Da non confondere col default del backend, che se la preferenza non è
 * impostata risponde `demo_and_trial`: quello è quanto ci dice lui quando
 * parla, questo è cosa facciamo noi quando non parla.
 */
export const FALLBACK_SIGNUP_MODE: SignupMode = 'trial_only';

const SIGNUP_MODES: SignupMode[] = [
  'demo_only',
  'demo_and_trial',
  'trial_only',
  'demo_masked_trial_only',
];

export const isSignupMode = (value: unknown): value is SignupMode =>
  typeof value === 'string' && SIGNUP_MODES.includes(value as SignupMode);

export interface CtaTarget {
  label: string;
  href: string;
  /**
   * La demo non naviga: apre il modal di prenotazione. `href` resta come
   * ripiego per chi non ha JavaScript e per l'apertura in una nuova scheda.
   */
  opensDemoModal?: boolean;
}

export const TRIAL_CTA: CtaTarget = { label: 'Prova gratis', href: SIGNUP_URL };
export const DEMO_CTA: CtaTarget = {
  label: 'Prenota una demo',
  href: DEMO_URL,
  opensDemoModal: true,
};
/** La prova di `demo_masked_trial_only`: si chiama prova, apre la prenotazione. */
export const MASKED_TRIAL_CTA: CtaTarget = { ...DEMO_CTA, label: 'Prova gratis' };

export interface SignupCtas {
  /** CTA principale: con entrambe attive vince la demo */
  primary: CtaTarget;
  /** Seconda CTA imposta dalla modalità; null quando ne è attiva una sola */
  secondary: CtaTarget | null;
  /** Riga di rassicurazione sotto i bottoni */
  note: string;
  /** La prova in autonomia, col link all'app */
  hasTrial: boolean;
  /** La prenotazione della videochiamata, nel modal */
  hasDemo: boolean;
  /**
   * La prenotazione si presenta come richiesta della prova gratuita
   * (`demo_masked_trial_only`): stesso modal, parole della prova.
   */
  demoAsTrial: boolean;
}

const TRIAL_NOTE = `${PLAN.trialDays} giorni di prova · Nessuna carta di credito`;
/**
 * Quanto dura la videochiamata, ovunque la si nomini. Lo slot a calendario
 * (`demo.slot_duration_minutes` sul backend) è più lungo e fa da margine: per
 * questo sulla landing si mostra solo l'ora d'inizio, mai quella di fine.
 */
export const DEMO_DURATION = '30–45 minuti';
export const DEMO_NOTE = `${DEMO_DURATION} in videochiamata · Nessun impegno`;
export const MASKED_TRIAL_NOTE = 'Gratis e senza impegno · Te la attiviamo in videochiamata';

/** Le CTA da mostrare per una data modalità. */
export function resolveSignupCtas(mode: SignupMode): SignupCtas {
  switch (mode) {
    case 'demo_only':
      return {
        primary: DEMO_CTA,
        secondary: null,
        note: DEMO_NOTE,
        hasTrial: false,
        hasDemo: true,
        demoAsTrial: false,
      };
    case 'demo_and_trial':
      return {
        primary: DEMO_CTA,
        secondary: TRIAL_CTA,
        note: TRIAL_NOTE,
        hasTrial: true,
        hasDemo: true,
        demoAsTrial: false,
      };
    case 'demo_masked_trial_only':
      return {
        primary: MASKED_TRIAL_CTA,
        secondary: null,
        note: MASKED_TRIAL_NOTE,
        hasTrial: false,
        hasDemo: true,
        demoAsTrial: true,
      };
    // Un valore che non conosciamo è un valore che non sappiamo leggere, e
    // quindi ricade nella regola di sopra: niente demo al buio.
    case 'trial_only':
    default:
      return {
        primary: TRIAL_CTA,
        secondary: null,
        note: TRIAL_NOTE,
        hasTrial: true,
        hasDemo: false,
        demoAsTrial: false,
      };
  }
}
